import React, { useState, useEffect } from 'react';
import { Paper, Box, Typography, Select, MenuItem, Switch, Tooltip, FormControlLabel, FormGroup } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import './metrics_below_map.css';

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#555',
    color: '#fff',
    fontSize: '0.8rem',
    borderRadius: '4px',
    padding: '6px',
  },
  '& .MuiTooltip-arrow': {
    color: '#555',
  },
}));

const MetricsInsideMap = ({ metricsNames, cqlFecha, weekNumber, issueTime, cqlSevere, handleRiskClick, onMenuOpen,   // <--- AÑADE ESTO
  onMenuClose }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useN1M, setUseN1M] = useState(false);
  const [selectedSevere, setSelectedSevere] = useState('Low');


  const removeN1mSuffix = (data) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => {
        const newKey = key.endsWith('_n1m') ? key.slice(0, -4) : key;
        return [newKey, value];
      })
    );
  };

  const formatPercentage = (value) => {
    if (typeof value === 'number') {
      return `${(value * 1).toFixed(3)}`;
    }
    return value;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    const endpoint = useN1M
      ? '/api/get_metrics_below_map_n1m'
      : '/api/get_metrics_below_map';
    const url = `${process.env.REACT_APP_API_URL}${endpoint}?cqlFecha=${encodeURIComponent(cqlFecha)}&cqlSevere=${encodeURIComponent(selectedSevere)}&weekNumber=${encodeURIComponent(weekNumber)}&metricsNames=${encodeURIComponent(metricsNames)}&issueTime=${encodeURIComponent(issueTime)}`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Data:', data);
      const processedData = useN1M
        ? data.map((item) => removeN1mSuffix(item))
        : data;
      setMetrics(processedData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cqlFecha && selectedSevere && weekNumber && metricsNames && issueTime) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [metricsNames, cqlFecha, selectedSevere, weekNumber, issueTime, useN1M]);

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="error" align="center">
          Error loading metrics.
        </Typography>
      </Box>
    );
  }

  const metricsData = Array.isArray(metrics) && metrics.length > 0 ? metrics[0] : {};

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column', // Forzamos columna para móvil
        alignItems: 'flex-start',
        bgcolor: 'transparent',
        p: 0,
        width: '100%',
        color: 'white',
        gap: 1, // Espacio reducido entre secciones
        overflow: 'visible',
      }}
    >
      {/* SECCIÓN 1: Switch Compacto */}
      <Box sx={{ width: '100%' }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={useN1M}
                onChange={() => setUseN1M(u => !u)}
                size="small"
                color="primary"
                sx={{ transform: 'scale(0.8)' }} // Más pequeño para móvil
              />
            }
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.75rem', fontWeight: 400, ml: -1 }}>
                Neighbourhood
                <CustomTooltip
                  title={<span style={{ fontSize: '0.7rem' }}><strong>Neighborhood Metrics</strong><br />
                      <p>
                        Include surrounding grid points to allow for small spatial shifts, giving credit when forecasts are slightly offset but still operationally useful.
                      </p>
                      <ul>
                        <li><strong>Precision</strong>: The proportion of predicted convection cases that were actually observed.</li>
                        <li><strong>Recall (Probability of Detection)</strong>: The proportion of observed convection cases that were correctly forecasted.</li>
                        <li><strong>F1 Score</strong>: The harmonic mean of precision and recall — a balanced measure that accounts for both missed events and false alarms.</li>
                        <li><strong>False Alarm Ratio (FAR)</strong>: The proportion of predicted convection events that did not occur.</li>
                        <li><strong>Gilbert Skill Score (GSS)</strong>: Adjusts for correct forecasts that could occur by chance, providing a fairer measure of skill for rare events.</li>
                      </ul>
                    </span>
                  }
                  arrow
                  placement="right"
                >
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'gray', ml: 0.5 }} />
                </CustomTooltip>
              </Box>
            }
            sx={{ m: 0 }}
          />
        </FormGroup>
      </Box>

      {/* SECCIÓN 2: Métricas en Filas Individuales */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
        {Object.entries(metricsData).map(([label, value]) => (
          <Box
            key={label}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1,
              py: 0.5,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
            }}
          >
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem', fontWeight: 600 }}>
              {label.toUpperCase()}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.75rem' }}>
              {formatPercentage(value)}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* SECCIÓN 3: Selector de Riesgo Reducido */}
      <Box sx={{ width: '100%', mt: 0.5, }} onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}>
        <Select
          onOpen={onMenuOpen}
          onClose={onMenuClose}
          value={selectedSevere}
          onChange={e => {
            // IMPORTANTE: Primero cambiamos el valor, luego ejecutamos la acción
            setSelectedSevere(e.target.value);
            handleRiskClick(e.target.value);
          }}
          fullWidth
          size="small"
          sx={{
            height: '32px',
            fontSize: '0.75rem',
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid #14599e',
            borderRadius:3,
            '& .MuiSelect-select': { paddingY: '4px' },
          }}
          MenuProps={{

            PaperProps: {
              sx: {
                bgcolor: '#2e2e2e64',
                color: 'white',
                zIndex: 99999,
                '& .MuiMenuItem-root': { fontSize: '0.8rem', minHeight: '32px' }
              }
            }
          }}
        >
          <MenuItem value="Low">Low</MenuItem>
          <MenuItem value="Medium">Medium</MenuItem>
          <MenuItem value="High">High</MenuItem>
        </Select>
      </Box>
    </Box>
  );
};

export default MetricsInsideMap;
